import React, {PropsWithChildren, useState} from 'react';
import {toast} from 'react-toastify';
import {useTranslation} from 'react-i18next';
import {useQueryClient} from '@tanstack/react-query';
import {QUERY_KEYS} from '../../../../utils/queryKeys';
import {randomString} from '../../../../utils/utils';
import Modal from '../../../../incase-domain/components/modals/directory/Modal';
import {resetPresAnalytics} from '../../../../service/analytics';
import i18n from '../../../../i18n';
import Button from '../../../../incase-domain/components/button/Button';
import Label from '../../../../incase-domain/components/label/Label';
import Prompt from '../../../../incase-domain/components/prompt/Prompt';

interface ResetReportsProps {
    selectedPresentationId: string;
}

function ResetReports(props: PropsWithChildren<ResetReportsProps>): JSX.Element {
    const {selectedPresentationId} = props;
    const {t} = useTranslation();
    const client = useQueryClient();

    const [resetReportModal, setResetReportModal] = useState(false);
    const [resetReportConfirmationString, setResetReportConfirmationString] = useState('');
    const [generatedValueState, setGeneratedValueState] = useState<string>(randomString(36));

    function resetAnalytics(): void {
        resetPresAnalytics(selectedPresentationId).then((res) => {
            if (res.success) {
                client.invalidateQueries({queryKey: QUERY_KEYS.analytics.all});
                toast.success(i18n.t('toasts.presAnalyticsResetSuccess'));
            } else {
                toast.error(i18n.t('toasts.presAnalyticsResetFail'));
            }
        });
    }

    return (
        <>
            <Button
                variant="solid"
                color="error"
                size="sm"
                leading="solar:trash-bin-trash-linear"
                onClick={() => setResetReportModal(true)}
            >
                {t('reportPage.resetReports')}
            </Button>

            <Modal
                icon="solar:restart-circle-bold"
                header={t('reportPage.modal.resetReportsHeader')}
                description={t('reportPage.modal.resetReportsDescription')}
                onCancel={() => {
                    setResetReportModal(false);
                    setResetReportConfirmationString('');
                    setGeneratedValueState(randomString(36));
                }}
                onConfirm={() => {
                    resetAnalytics();
                    setResetReportModal(false);
                }}
                showCancel
                cancelText={t('common.cancel')}
                showConfirm
                confirmText={t('common.continue')}
                isOpen={resetReportModal}
                size="sm"
                confirmDisabled={resetReportConfirmationString.toLowerCase() !== generatedValueState.toLowerCase()}
            >
                <Prompt label={t('reportPage.modal.resetReportsBody')} type="error" />
                <div className="u-mt-4 u-flex u-flex-col u-gap-2">
                    <div className="c-sidepanel__input">
                        <input type="text" readOnly defaultValue={generatedValueState} />
                    </div>
                    <div>
                        <Label>{t('reportPage.modal.confirmReset')}</Label>
                        <div className="c-sidepanel__input">
                            <input
                                type="text"
                                placeholder={t('reportPage.modal.generatedStringPlaceholder')}
                                value={resetReportConfirmationString}
                                onChange={(e) => setResetReportConfirmationString(e.target.value)}
                                onPaste={(e) => e.preventDefault()}
                                onDrop={(e) => e.preventDefault()}
                            />
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
}

export default ResetReports;
